import { AllSteps, AnswerId, Answers, Steps, UNLIMITED_DATA } from '../state/interfaces'
import { factorMap } from './factorMap'

export interface InternetUsageRequest {
  answers: Answers
  included?: AnswerId[]
}

export interface InternetUsageResponse {
  downloadSpeed: number
  monthlyUsageInKbps: number | UNLIMITED_DATA
}

export function calculateInternetUsageInKbps({ answers }: InternetUsageRequest): InternetUsageResponse {
  const MIN_SPEED_REQUIREMENT = 10
  const USAGE_NEED_FACTOR = 31
  const INITIAL_FACTOR_FOR_PEOPLE_AND_DEVICE = 1
  const included: Steps[] = [
    Steps.HOW_MANY_PEOPLE_SIMULTANEOUSLY,
    Steps.HOW_MANY_DEVICES,
    Steps.HOW_MANY_PEOPLE_WORKING_FROM_HOME,
    Steps.STREAMING_VIDEO_QUALITY,
    Steps.ONLINE_GAMING,
    Steps.LARGE_FILES_DOWNLOAD_FREQUENCY,
  ]

  const correctedAnswers = deleteNotIncludedAnswerProperties({ answers, included })

  const factorForDevicesAndPeople: number = getFactorForDevicesAndPeople(
    correctedAnswers,
    INITIAL_FACTOR_FOR_PEOPLE_AND_DEVICE,
  )
  const downloadSpeed: number = getDownloadSpeed(factorForDevicesAndPeople, correctedAnswers, [MIN_SPEED_REQUIREMENT])
  const monthlyUsageInKbps: number | UNLIMITED_DATA = getMonthlyUsage(
    factorForDevicesAndPeople,
    correctedAnswers,
    USAGE_NEED_FACTOR,
  )

  return { downloadSpeed, monthlyUsageInKbps }
}

const getFactorForDevicesAndPeople = (answers: Answers, initialFactor: number) => {
  let factorForDevicesAndPeople: number = initialFactor

  Object.entries(answers).forEach(([stepId, answerId]) => {
    if (!Object.keys(factorMap.weight).includes(stepId)) {
      factorForDevicesAndPeople *= factorMap.factor[stepId][answerId[0]]
    }
  })
  return factorForDevicesAndPeople
}

const getDownloadSpeed = (factorForDevicesPeople: number, answers: Answers, minSpeedRequirement: number[]) => {
  const speedNeeds = minSpeedRequirement // to set a minimum
  Object.entries(answers).forEach(([stepId, answerId]) => {
    if (Object.keys(factorMap.weight).includes(stepId)) {
      const need = factorMap.weight[stepId][answerId[0]] * factorForDevicesPeople
      speedNeeds.push(need)
    }
  })
  return Math.floor(Math.max(...speedNeeds))
}

const getMonthlyUsage = (
  factorForDevicesAndPeople: number,
  answers: Answers,
  needFactor: number,
): number | UNLIMITED_DATA => {
  const bandwidthNeeds: number[] = []
  Object.entries(answers).forEach(([stepId, answerId]) => {
    if (Object.keys(factorMap.dataConsumption).includes(stepId)) {
      const need = factorMap.dataConsumption[stepId][answerId[0]] * needFactor * factorForDevicesAndPeople
      bandwidthNeeds.push(need)
    }
  })
  const totalMbConsumption = bandwidthNeeds.reduce((accumulate, val) => accumulate + val)
  return totalMbConsumption > 500000 ? 'unlimited-data' : Math.floor(totalMbConsumption / 1000) * 1000
}

const deleteNotIncludedAnswerProperties = ({ answers, included = [] }: InternetUsageRequest) => {
  const answersUsedForTheCalcul: Partial<Record<AllSteps, string[]>> = {}

  included.forEach((includedAnswer) => {
    if (answers.hasOwnProperty(includedAnswer)) {
      answersUsedForTheCalcul[includedAnswer as Steps] = answers[includedAnswer as Steps]
    }
  })
  return answersUsedForTheCalcul
}
